import { useState } from "react";
import {
  FX_DEFAULT_PARAMS,
  normalizeFxNode,
  type FxNode,
  type FxType,
} from "../../../../src/lib/audio";
import {
  defaultWetForFxType,
  formatFxParam,
  FX_PARAM_LABELS,
  FX_PARAM_OPTIONS,
  FX_PARAM_RANGES,
  FX_PARAM_TOGGLES,
  FX_TYPE_LABELS,
  FX_TYPES,
} from "../lib/fxMeta";
import { Checkbox } from "./ui/Checkbox";
import { LearnableControl } from "./LearnableControl";
import { Slider } from "./ui/Slider";

let fxIdSeq = 0;

function newFxId(): string {
  fxIdSeq += 1;
  return `fx-${Date.now().toString(36)}-${fxIdSeq.toString(36)}`;
}

function withFxId(fx: FxNode): FxNode {
  return fx.id ? fx : { ...fx, id: newFxId() };
}

export type VerticalFxStripProps = {
  effects: FxNode[];
  onCommit: (next: FxNode[], status?: string) => void;
  /** Master bus: show bypass control. */
  bypass?: boolean;
  onBypassChange?: (on: boolean) => void;
  emptyHint?: string;
  /** e.g. `/master/fx` or `/track/0/fx` — enables Learn on wet. */
  learnFxPrefix?: string;
  /** e.g. `/master/fxBypass` — Learn on master bypass. */
  learnBypassPath?: string;
};

/**
 * Vertical insert stack for the Mixer: each effect is a collapsed accordion.
 * Params expand only when opened — not the horizontal FxDock card strip.
 */
export function VerticalFxStrip({
  effects,
  onCommit,
  bypass,
  onBypassChange,
  emptyHint = "Empty — dry. Add an effect below.",
  learnFxPrefix,
  learnBypassPath,
}: VerticalFxStripProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  function commit(next: FxNode[], status?: string): void {
    onCommit(next.map(fx => withFxId(normalizeFxNode(fx))), status);
  }

  function updateAt(index: number, patch: Partial<FxNode> | ((fx: FxNode) => FxNode)): void {
    const next = effects.map((fx, i) => {
      if (i !== index) {
        return fx;
      }
      return typeof patch === "function" ? patch(fx) : normalizeFxNode({ ...fx, ...patch });
    });
    commit(next);
  }

  function move(index: number, delta: number): void {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= effects.length) {
      return;
    }
    const next = effects.slice();
    const swap = next[index]!;
    next[index] = next[nextIndex]!;
    next[nextIndex] = swap;
    commit(next);
  }

  function addEffect(type: FxType): void {
    const node = withFxId(
      normalizeFxNode({ type, wet: defaultWetForFxType(type) }),
    );
    commit([...effects, node], `Added ${FX_TYPE_LABELS[type]}`);
    if (node.id) {
      setOpenIds(prev => new Set(prev).add(node.id!));
    }
  }

  function toggleOpen(id: string): void {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const keyed = effects.map((fx, index) => ({
    fx,
    key: fx.id ?? `fx-index-${index}`,
    index,
  }));

  return (
    <div className="flex min-h-0 flex-col gap-1.5">
      {onBypassChange ? (
        learnBypassPath ? (
          <LearnableControl path={learnBypassPath}>
            <button
              type="button"
              title={bypass ? "FX stack bypassed — click to enable" : "Bypass FX stack"}
              aria-pressed={bypass === true}
              className={`w-full rounded border px-2 py-1 text-[10px] font-semibold ${
                bypass
                  ? "border-amber-400/80 bg-amber-400 text-slate-900"
                  : "border-lab-line bg-transparent text-lab-muted hover:border-white/30 hover:text-slate-200"
              }`}
              onClick={() => onBypassChange(!bypass)}
            >
              Bypass
            </button>
          </LearnableControl>
        ) : (
          <button
            type="button"
            title={bypass ? "FX stack bypassed — click to enable" : "Bypass FX stack"}
            aria-pressed={bypass === true}
            className={`w-full rounded border px-2 py-1 text-[10px] font-semibold ${
              bypass
                ? "border-amber-400/80 bg-amber-400 text-slate-900"
                : "border-lab-line bg-transparent text-lab-muted hover:border-white/30 hover:text-slate-200"
            }`}
            onClick={() => onBypassChange(!bypass)}
          >
            Bypass
          </button>
        )
      ) : null}

      <div className={bypass ? "opacity-45" : undefined}>
        {keyed.length === 0 ? (
          <p className="m-0 text-[10px] leading-snug text-lab-muted">{emptyHint}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {keyed.map(({ fx, key, index }) => {
              const expanded = openIds.has(key);
              return (
                <li key={key}>
                  <FxAccordion
                    fx={fx}
                    index={index}
                    count={keyed.length}
                    expanded={expanded}
                    learnFxPrefix={learnFxPrefix}
                    onToggle={() => toggleOpen(key)}
                    onUpdate={patch => updateAt(index, patch)}
                    onMove={delta => move(index, delta)}
                    onRemove={() => {
                      commit(
                        effects.filter((_, i) => i !== index),
                        "Removed FX node",
                      );
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <select
        aria-label="Add effect"
        className="w-full rounded border border-lab-line bg-lab-bg px-1.5 py-1 text-[11px] text-lab-text"
        defaultValue=""
        onChange={event => {
          const type = event.target.value as FxType;
          if (!type) {
            return;
          }
          addEffect(type);
          event.target.value = "";
        }}
      >
        <option value="">Add effect…</option>
        {FX_TYPES.map(type => (
          <option key={type} value={type}>
            {FX_TYPE_LABELS[type]}
          </option>
        ))}
      </select>
    </div>
  );
}

function FxAccordion({
  fx,
  index,
  count,
  expanded,
  learnFxPrefix,
  onToggle,
  onUpdate,
  onMove,
  onRemove,
}: {
  fx: FxNode;
  index: number;
  count: number;
  expanded: boolean;
  learnFxPrefix?: string;
  onToggle: () => void;
  onUpdate: (patch: Partial<FxNode> | ((node: FxNode) => FxNode)) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const defaults = FX_DEFAULT_PARAMS[fx.type];
  const labels = FX_PARAM_LABELS[fx.type] ?? {};
  const selectClass =
    "w-full rounded border border-lab-line bg-[#12161c] px-[0.3rem] py-0.5 text-[11px] text-slate-200 [font:inherit]";

  return (
    <section
      className={`group/fx overflow-hidden rounded-md border border-lab-line bg-lab-panel-deep ${
        fx.enabled ? "" : "opacity-55"
      }`}
    >
      <div className="relative flex items-center gap-1 px-1.5 py-1">
        {learnFxPrefix ? (
          <LearnableControl path={`${learnFxPrefix}/${index}/enabled`}>
            <Checkbox
              variant="power"
              checked={fx.enabled}
              title={fx.enabled ? "Disable effect" : "Enable effect"}
              aria-label={fx.enabled ? "Disable effect" : "Enable effect"}
              onCheckedChange={enabled => onUpdate({ enabled })}
            />
          </LearnableControl>
        ) : (
          <Checkbox
            variant="power"
            checked={fx.enabled}
            title={fx.enabled ? "Disable effect" : "Enable effect"}
            aria-label={fx.enabled ? "Disable effect" : "Enable effect"}
            onCheckedChange={enabled => onUpdate({ enabled })}
          />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 border-0 bg-transparent px-0.5 py-0.5 text-left text-[11px] font-semibold text-lab-text hover:text-white"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span className="w-3 shrink-0 text-[10px] text-lab-muted" aria-hidden>
            {expanded ? "▾" : "▸"}
          </span>
          <span className="min-w-0 flex-1 truncate">{FX_TYPE_LABELS[fx.type]}</span>
          <span className="shrink-0 tabular-nums text-[10px] font-normal text-lab-muted">
            {Math.round(fx.wet * 100)}%
          </span>
        </button>
        <div
          className="pointer-events-none absolute top-1/2 right-1 flex -translate-y-1/2 gap-px rounded border border-lab-line bg-lab-panel-deep/95 opacity-0 shadow-[0_2px_8px_rgba(0,0,0,0.35)] transition-opacity group-hover/fx:pointer-events-auto group-hover/fx:opacity-100 group-focus-within/fx:pointer-events-auto group-focus-within/fx:opacity-100"
        >
          <button
            type="button"
            className="min-w-5 px-0.5 py-0.5 text-[10px] text-lab-muted hover:text-lab-text disabled:opacity-30"
            disabled={index === 0}
            title="Move up"
            aria-label="Move up"
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="min-w-5 px-0.5 py-0.5 text-[10px] text-lab-muted hover:text-lab-text disabled:opacity-30"
            disabled={index === count - 1}
            title="Move down"
            aria-label="Move down"
            onClick={() => onMove(1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="min-w-5 px-0.5 py-0.5 text-[11px] text-lab-muted hover:text-rose-300"
            title="Remove"
            aria-label="Remove effect"
            onClick={onRemove}
          >
            ×
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="flex flex-col gap-1 border-t border-lab-line/70 px-1.5 pt-1.5 pb-2">
          <label className="grid grid-cols-[3.2rem_1fr_auto] items-center gap-1 text-[10px] text-lab-muted">
            <span>Type</span>
            <select
              className={`${selectClass} col-span-2`}
              value={fx.type}
              onChange={event => {
                const nextType = event.target.value as FxType;
                onUpdate(
                  normalizeFxNode({
                    id: fx.id,
                    type: nextType,
                    enabled: fx.enabled,
                    wet: fx.wet,
                  }),
                );
              }}
            >
              {FX_TYPES.map(type => (
                <option key={type} value={type}>
                  {FX_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </label>

          <label className="grid grid-cols-[3.2rem_1fr_auto] items-center gap-1 text-[10px] text-lab-muted">
            <span>Wet</span>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={fx.wet}
              aria-label="Wet"
              learnPath={learnFxPrefix ? `${learnFxPrefix}/${index}/wet` : undefined}
              onChange={wet => onUpdate({ wet })}
            />
            <span className="min-w-[2rem] text-right tabular-nums text-[10px] text-slate-300">
              {fx.wet.toFixed(2)}
            </span>
          </label>

          {Object.keys(defaults).map(key => {
            const value = fx.params[key] ?? defaults[key]!;
            const paramPath = learnFxPrefix
              ? `${learnFxPrefix}/${index}/params/${key}`
              : undefined;
            if (FX_PARAM_TOGGLES.has(key)) {
              const toggle = (
                <Checkbox
                  className="justify-self-start"
                  checked={value >= 0.5}
                  aria-label={labels[key] ?? key}
                  onCheckedChange={on => {
                    const v = on ? 1 : 0;
                    onUpdate(node => ({
                      ...node,
                      params: { ...node.params, [key]: v },
                    }));
                  }}
                />
              );
              return (
                <label
                  key={key}
                  className="grid grid-cols-[3.2rem_1fr] items-center gap-1 text-[10px] text-lab-muted"
                >
                  <span>{labels[key] ?? key}</span>
                  {paramPath ? (
                    <LearnableControl path={paramPath}>{toggle}</LearnableControl>
                  ) : (
                    toggle
                  )}
                </label>
              );
            }
            const options = FX_PARAM_OPTIONS[fx.type]?.[key];
            if (options) {
              const select = (
                <select
                  className={selectClass}
                  value={Math.round(value)}
                  onChange={event => {
                    const v = Number(event.target.value);
                    onUpdate(node => ({
                      ...node,
                      params: { ...node.params, [key]: v },
                    }));
                  }}
                >
                  {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              );
              return (
                <label
                  key={key}
                  className="grid grid-cols-[3.2rem_1fr] items-center gap-1 text-[10px] text-lab-muted"
                >
                  <span>{labels[key] ?? key}</span>
                  {paramPath ? (
                    <LearnableControl path={paramPath}>{select}</LearnableControl>
                  ) : (
                    select
                  )}
                </label>
              );
            }
            const range = FX_PARAM_RANGES[key] ?? { min: 0, max: 1, step: 0.01 };
            return (
              <label
                key={key}
                className="grid grid-cols-[3.2rem_1fr_auto] items-center gap-1 text-[10px] text-lab-muted"
              >
                <span className="truncate">{labels[key] ?? key}</span>
                <Slider
                  min={range.min}
                  max={range.max}
                  step={range.step}
                  value={value}
                  aria-label={labels[key] ?? key}
                  learnPath={paramPath}
                  onChange={v => {
                    onUpdate(node => ({
                      ...node,
                      params: { ...node.params, [key]: v },
                    }));
                  }}
                />
                <span className="min-w-[2rem] text-right tabular-nums text-[10px] text-slate-300">
                  {formatFxParam(value)}
                </span>
              </label>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
